import React, { useState, useEffect, useCallback } from "react";
import { fetchCurrentUserInfo, fetchCommissions, countCommissions, fetchFullProfile, fetchCommissionStats, fetchCommissionFromUsers } from "../script/rpc";
import { useAuth } from "../App";
import Pagination from "./Pagination";

const Icons = {
  wallet: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M12 11h8v4h-8z" /><circle cx="16" cy="13" r="1" />
    </svg>
  ),
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
};

export default function CommissionsPage() {
  const { auth } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // 'all', 'paid', 'unpaid'
  const [monthFilter, setMonthFilter] = useState(""); // 'YYYY-MM'
  const [amountFilter, setAmountFilter] = useState(0); // min amount
  const [selectedFromUser, setSelectedFromUser] = useState("");
  const [fromUsers, setFromUsers] = useState([]);
  const [isGrouped, setIsGrouped] = useState(false);
  const [sortOrder, setSortOrder] = useState("trans_date desc, id desc");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    unpaid: 0
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const info = await fetchCurrentUserInfo(auth.uid, auth.password);
      if (info?.partner_id) {
        const pid = info.partner_id[0];
        const offset = (currentPage - 1) * itemsPerPage;
        const minAmt = parseFloat(amountFilter) || 0;

        // Fetch paginated data + Summary Stats
        const [count, data, dynamicStats] = await Promise.all([
          countCommissions(auth.uid, auth.password, pid, { filter, month: monthFilter, minAmount: minAmt, fromUserId: selectedFromUser }),
          fetchCommissions(auth.uid, auth.password, pid, { offset, limit: itemsPerPage, filter, month: monthFilter, minAmount: minAmt, sort: sortOrder, fromUserId: selectedFromUser }),
          fetchCommissionStats(auth.uid, auth.password, pid, { month: monthFilter, minAmount: minAmt, fromUserId: selectedFromUser })
        ]);

        setTotalItems(count || 0);
        setCommissions(data || []);

        if (dynamicStats) {
          setStats({
            total: dynamicStats.total || 0,
            paid: dynamicStats.paid || 0,
            unpaid: dynamicStats.unpaid || 0
          });
        }
      } else {
        setCommissions([]);
        setTotalItems(0);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [auth.uid, auth.password, currentPage, filter, monthFilter, amountFilter, sortOrder, selectedFromUser]);

  useEffect(() => {
    const loadFromUsers = async () => {
      try {
        const info = await fetchCurrentUserInfo(auth.uid, auth.password);
        if (info?.partner_id) {
          const list = await fetchCommissionFromUsers(auth.uid, auth.password, info.partner_id[0]);
          setFromUsers(list || []);
        }
      } catch (err) { console.error(err); }
    };
    loadFromUsers();
  }, [auth.uid, auth.password]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, monthFilter, amountFilter, selectedFromUser]);

  const generateMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d.toLocaleString("en-IN", { month: "long", year: "numeric" });
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const value = `${year}-${month}`; // YYYY-MM (Local)
      months.push({ label, value });
    }
    return months;
  };
  const availableMonths = generateMonths();

  const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { style: "currency", currency: "INR" });

  const formatDateTime = (v) => {
    if (!v) return "—";
    const d = new Date(v + " UTC"); // Odoo usually returns UTC
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const initials = (name = "") => name.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");

  const groupCommissions = (data) => {
    if (!isGrouped) return [{ name: null, items: data, total: 0, count: data.length }];
    const groups = [];
    data.forEach((comm) => {
      const name = comm.child_partner_id?.[1] || "Others";
      const userId = comm.child_partner_id?.[0];
      
      let g = groups.find((x) => x.name === name);
      if (!g) {
        // Look up server-side totals from fromUsers list
        const serverStats = fromUsers.find(u => u.id === userId);
        g = { 
          name, 
          items: [], 
          total: serverStats?.totalAmount || 0, 
          count: serverStats?.count || 0 
        };
        groups.push(g);
      }
      g.items.push(comm);
    });
    return groups;
  };

  return (
    <main className="dp-main">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px", margin: 0 }}>My Commissions</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14 }}>Track your earnings and rewards</p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              style={{
                appearance: "none",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "10px 40px 10px 16px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#1e293b",
                cursor: "pointer",
                outline: "none",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                transition: "all 0.2s ease"
              }}>
              <option value="">All Months</option>
              {availableMonths.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#64748b" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </div>
          </div>
        </div>
      </div>


      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        marginBottom: 24
      }}>
        {/* Total Commission */}
        <div
          onClick={() => setFilter("all")}
          style={{
            background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
            padding: "18px 20px",
            borderRadius: 16,
            color: "#fff",
            boxShadow: filter === "all" ? "0 10px 20px -5px rgba(99, 102, 241, 0.4)" : "0 10px 15px -5px rgba(99, 102, 241, 0.25)",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 100,
            cursor: "pointer",
            transform: filter === "all" ? "scale(1.02)" : "scale(1)",
            transition: "all 0.2s ease",
            border: filter === "all" ? "2px solid #fff" : "2px solid transparent"
          }}>
          <div style={{ position: "absolute", top: -16, right: -16, width: 90, height: 90, background: "rgba(255,255,255,0.08)", borderRadius: "50%" }} />
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, opacity: 0.85, textTransform: "uppercase", letterSpacing: "1px" }}>Total Commission</p>
          <h2 style={{ margin: "6px 0 0", fontSize: 24, fontWeight: 800, letterSpacing: "-0.5px" }}>{fmt(stats.total)}</h2>
        </div>

        {/* Paid Commission */}
        <div
          onClick={() => setFilter("paid")}
          style={{
            background: "#fff",
            padding: "18px 20px",
            borderRadius: 16,
            border: filter === "paid" ? "2px solid #22c55e" : "1px solid #f1f5f9",
            boxShadow: filter === "paid" ? "0 8px 16px -4px rgba(34, 197, 94, 0.15)" : "0 4px 6px -2px rgba(0, 0, 0, 0.04)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 100,
            cursor: "pointer",
            transform: filter === "paid" ? "scale(1.02)" : "scale(1)",
            transition: "all 0.2s ease"
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", color: "#22c55e", flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
            </div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#64748b" }}>Paid Commission</p>
          </div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.5px" }}>{fmt(stats.paid)}</h2>
        </div>

        {/* Unpaid Commission */}
        <div
          onClick={() => setFilter("unpaid")}
          style={{
            background: "#fff",
            padding: "18px 20px",
            borderRadius: 16,
            border: filter === "unpaid" ? "2px solid #f97316" : "1px solid #f1f5f9",
            boxShadow: filter === "unpaid" ? "0 8px 16px -4px rgba(249, 115, 22, 0.15)" : "0 4px 6px -2px rgba(0, 0, 0, 0.04)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 100,
            cursor: "pointer",
            transform: filter === "unpaid" ? "scale(1.02)" : "scale(1)",
            transition: "all 0.2s ease"
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fff7ed", display: "flex", alignItems: "center", justifyContent: "center", color: "#f97316", flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            </div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#64748b" }}>Unpaid Commission</p>
          </div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.5px" }}>{fmt(stats.unpaid)}</h2>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 20, border: "0.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", position: "relative" }}>
        {/* Local Loader overlay */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 10,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(255, 255, 255, 0.6)", backdropFilter: "blur(2px)",
          opacity: (loading && commissions.length > 0) ? 1 : 0,
          pointerEvents: (loading && commissions.length > 0) ? "auto" : "none",
          transition: "opacity 0.2s ease"
        }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid #f1f5f9", borderTopColor: "#7c3aed", animation: "dp-spin 0.8s linear infinite" }} />
        </div>

        {loading && commissions.length === 0 ? (
          <div style={{ padding: "80px 0", textAlign: "center", color: "#64748b" }}>
            <div style={{ width: 40, height: 40, border: "3px solid #f1f5f9", borderTopColor: "#7c3aed", borderRadius: "50%", animation: "dp-spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            <p style={{ fontWeight: 600 }}>Loading commissions...</p>
          </div>
        ) : error ? (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <p style={{ color: "#ef4444", fontWeight: 700, margin: 0 }}>Error: {error}</p>
            <button onClick={loadData} style={{ marginTop: 20, padding: "10px 24px", borderRadius: 10, border: "none", background: "#7c3aed", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Try Again</button>
          </div>
        ) : commissions.length === 0 ? (
          <div style={{ padding: "80px 24px", textAlign: "center", color: "#94a3b8" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>💰</div>
            <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>No commissions found</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto", opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", minWidth: 1100 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
                    <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px" }}>Transaction Date</th>
                    <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px" }}>Ref Transaction</th>
                    <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px" }}>Commission To</th>
                    <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px" }}>Commission From</th>
                    <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px" }}>Post Incentive</th>
                    <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px" }}>Level</th>
                    <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", textAlign: "right" }}>Amount</th>
                    <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", textAlign: "center" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {groupCommissions(commissions).map((group, groupIdx) => (
                    <React.Fragment key={group.name || groupIdx}>
                      {isGrouped && group.name && (
                        <tr style={{ background: "#f8fafc", borderLeft: "4px solid #6366f1" }}>
                          <td colSpan="8" style={{ padding: "12px 20px" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#eef2ff", color: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12 }}>
                                  {initials(group.name)}
                                </div>
                                <span style={{ fontWeight: 800, color: "#1e293b", fontSize: 14 }}>{group.name}</span>
                                <span style={{ background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                                  {group.count} Total Commissions
                                </span>
                              </div>
                              <div style={{ fontWeight: 800, color: "#6366f1", fontSize: 13 }}>
                                Lifetime Total: {fmt(group.total)}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {group.items.map((comm, idx) => (
                        <tr key={comm.id} style={{ borderBottom: idx < group.items.length - 1 || !isGrouped ? "1px solid #f8fafc" : "none", transition: "background 0.2s" }}>
                          <td style={{ padding: "16px 20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>
                                {formatDateTime(comm.create_date || comm.trans_date)}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "16px 20px" }}>
                            <span style={{ fontWeight: 700, color: "#6366f1", background: "#f5f3ff", padding: "4px 10px", borderRadius: 6, fontSize: 13 }}>
                              {comm.transaction_id?.[1] || "—"}
                            </span>
                          </td>
                          <td style={{ padding: "16px 20px" }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                              {comm.partner_id?.[1] || "—"}
                            </span>
                          </td>
                          <td style={{ padding: "16px 20px" }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                              {comm.child_partner_id?.[1] || "—"}
                            </span>
                          </td>
                          <td style={{ padding: "16px 20px" }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#6366f1", background: "#f5f3ff", padding: "4px 10px", borderRadius: 6 }}>
                              {comm.post_id?.[1] || "—"}
                            </span>
                          </td>
                          <td style={{ padding: "16px 20px" }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#6366f1", background: "#f5f3ff", padding: "4px 10px", borderRadius: 6 }}>
                              {comm.level_name || "—"}
                            </span>
                          </td>
                          <td style={{
                            padding: "16px 20px", textAlign: "right", fontWeight: 800,
                            color: "#475569",
                            fontSize: 15
                          }}>
                            {fmt(comm.commission_amount)}
                          </td>
                          <td style={{ padding: "16px 20px", textAlign: "center" }}>
                            <span style={{
                              display: "inline-block",
                              padding: "4px 12px",
                              borderRadius: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              background: comm.is_paid ? "#ecfdf5" : "#fff7ed",
                              color: comm.is_paid ? "#059669" : "#f97316",
                              border: `1px solid ${comm.is_paid ? "#a7f3d0" : "#ffedd5"}`
                            }}>
                              {comm.is_paid ? "Paid" : "Unpaid"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {totalItems > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            )}
          </>
        )}
      </div>
    </main>
  );
}
